/* biome-ignore-all lint/suspicious/noExplicitAny: reading practice log detail */
/* biome-ignore-all lint/suspicious/noArrayIndexKey: question and answer lists */
import type React from "react"
import { useEffect, useMemo } from "react"
import { Modal, Tag, Typography, Button, Space, Divider, Progress, Table, Empty, Badge, Alert } from "antd"
import { LeftOutlined, RightOutlined, BookOutlined, QuestionCircleOutlined } from "@ant-design/icons"
import { IconCheck, IconX, IconMinus, IconVocabulary, IconBulb, IconFileText } from "@tabler/icons-react"
import { UserCellLink } from "@/components/UserCellLink"
import type { ReadingPracticeLogItem } from "../types"
import { DIFFICULTY_MAP, QUESTION_TYPE_MAP } from "./ReadingPracticeTable"
import dayjs from "dayjs"
import "@/styles/table.full.css"

const { Paragraph } = Typography

export interface ReadingPracticeDetailModalProps {
  open: boolean
  record: ReadingPracticeLogItem | null
  items?: ReadingPracticeLogItem[]
  currentIndex?: number
  onNavigate?: (index: number) => void
  onClose: () => void
}

export const ReadingPracticeDetailModal: React.FC<ReadingPracticeDetailModalProps> = ({
  open,
  record,
  items = [],
  currentIndex = 0,
  onNavigate,
  onClose,
}) => {
  const totalRecords = items.length
  const hasPrev = typeof currentIndex === "number" && currentIndex > 0
  const hasNext = typeof currentIndex === "number" && currentIndex < totalRecords - 1

  const handlePrev = () => {
    if (hasPrev && onNavigate) {
      onNavigate(currentIndex - 1)
    }
  }

  const handleNext = () => {
    if (hasNext && onNavigate) {
      onNavigate(currentIndex + 1)
    }
  }

  // Keyboard shortcut: ArrowLeft / ArrowRight to navigate logs
  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      const tagName = target?.tagName?.toUpperCase()
      if (tagName === "INPUT" || tagName === "TEXTAREA" || target?.isContentEditable) {
        return
      }

      if (e.key === "ArrowLeft") {
        e.preventDefault()
        handlePrev()
      } else if (e.key === "ArrowRight") {
        e.preventDefault()
        handleNext()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [open, hasPrev, hasNext, currentIndex, onNavigate])

  const rc = record?.reading_content
  const diffInfo = rc?.difficulty ? DIFFICULTY_MAP[rc.difficulty] : null

  const isInit = record?.status === "init" || (!record?.status && !record?.completed_at)

  // Format time_spent_seconds
  const durationText = useMemo(() => {
    const sec = record?.time_spent_seconds
    if (sec == null || Number.isNaN(sec)) return null
    const mins = Math.floor(sec / 60)
    const secs = sec % 60
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`
  }, [record?.time_spent_seconds])

  // Calculate score accuracy
  const correct = record?.correct_count ?? 0
  const wrong = record?.wrong_count ?? 0
  const skipped = record?.skipped_count ?? 0
  const total = correct + wrong + skipped
  const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0

  // Highlight passage text with target vocabularies
  const highlightedPassage = useMemo(() => {
    if (!rc?.passage_text) return null
    const vocabList = rc.target_vocabularies || []
    if (!vocabList.length) {
      return (
        <Paragraph className="whitespace-pre-line text-gray-800 text-sm leading-relaxed">{rc.passage_text}</Paragraph>
      )
    }

    // Escape regex characters
    const escapedVocabs = vocabList
      .filter(Boolean)
      .map((v) => v.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .sort((a, b) => b.length - a.length)

    if (!escapedVocabs.length) {
      return (
        <Paragraph className="whitespace-pre-line text-gray-800 text-sm leading-relaxed">{rc.passage_text}</Paragraph>
      )
    }

    const regex = new RegExp(`\\b(${escapedVocabs.join("|")})\\b`, "gi")
    const parts = rc.passage_text.split(regex)

    return (
      <div className="whitespace-pre-line font-sans text-gray-800 text-sm leading-relaxed">
        {parts.map((part, i) => {
          const isTarget = vocabList.some((v) => v.toLowerCase() === part.toLowerCase())
          if (isTarget) {
            return (
              <mark
                key={i}
                className="rounded border border-amber-200 bg-amber-100 px-1 py-0.5 font-semibold text-amber-900"
              >
                {part}
              </mark>
            )
          }
          return part
        })}
      </div>
    )
  }, [rc?.passage_text, rc?.target_vocabularies])

  // Breakdown Columns for Table
  const breakdownColumns = [
    {
      title: "Dạng câu hỏi",
      dataIndex: "typeName",
      key: "typeName",
      render: (val: string) => {
        const info = QUESTION_TYPE_MAP[val]
        return (
          <Tag color={info?.color || "default"} className="font-medium text-xs">
            {info?.label || val}
          </Tag>
        )
      },
    },
    {
      title: "Đúng",
      dataIndex: "correct",
      key: "correct",
      align: "center" as const,
      render: (val: number) => <span className="font-bold font-mono text-emerald-600">{val}</span>,
    },
    {
      title: "Sai",
      dataIndex: "wrong",
      key: "wrong",
      align: "center" as const,
      render: (val: number) => <span className="font-bold font-mono text-rose-600">{val}</span>,
    },
    {
      title: "Bỏ qua",
      dataIndex: "skipped",
      key: "skipped",
      align: "center" as const,
      render: (val: number) => <span className="font-bold font-mono text-amber-600">{val}</span>,
    },
    {
      title: "Tổng câu",
      dataIndex: "total",
      key: "total",
      align: "center" as const,
      render: (val: number) => <span className="font-mono text-gray-700">{val}</span>,
    },
    {
      title: "Độ chính xác",
      key: "accuracy",
      align: "center" as const,
      render: (_: any, row: any) => {
        const acc = row.total > 0 ? Math.round((row.correct / row.total) * 100) : 0
        return (
          <div className="flex items-center justify-center gap-1.5">
            <span className="font-mono font-semibold text-xs">{acc}%</span>
            <Progress
              percent={acc}
              size="small"
              showInfo={false}
              className="m-0 w-12"
              status={acc >= 80 ? "success" : acc >= 50 ? "normal" : "exception"}
            />
          </div>
        )
      },
    },
  ]

  const questions = rc?.questions || {}
  const clozeQuestions = questions.cloze || []
  const mcqQuestions = questions.mcq || []
  const tfQuestions = questions.tf || []

  // User answers helpers
  const userAnswers = record?.user_answers
  const userCloze = userAnswers?.cloze || {}
  const userMcq = userAnswers?.mcq || {}
  const userTf = userAnswers?.tf || {}

  const isClozeAnswerCorrect = (q: { correct_answer: string; accepted_answers?: string[] }, userAns?: string) => {
    if (!userAns) return false
    const trimmed = userAns.trim().toLowerCase()
    if (trimmed === q.correct_answer.trim().toLowerCase()) return true
    if (q.accepted_answers?.some((a) => a.trim().toLowerCase() === trimmed)) return true
    return false
  }

  const isMcqAnswerCorrect = (qAnswer: string, userKey?: string) => {
    if (!userKey || !qAnswer) return false
    return userKey.trim().toLowerCase() === qAnswer.trim().toLowerCase()
  }

  const isTfAnswerCorrect = (qAnswer: string, userAns?: string) => {
    if (!userAns || !qAnswer) return false
    return userAns.trim().toLowerCase() === String(qAnswer).trim().toLowerCase()
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width="100vw"
      style={{
        top: 0,
        left: 0,
        margin: 0,
        padding: 0,
        maxWidth: "100vw",
        height: "100vh",
      }}
      styles={{
        wrapper: {
          overflow: "hidden",
        },
        container: {
          height: "100vh",
          maxHeight: "100vh",
          width: "100vw",
          maxWidth: "100vw",
          borderRadius: 0,
          display: "flex",
          flexDirection: "column",
          padding: "14px 18px 10px 18px",
          overflow: "hidden",
        },
        header: {
          margin: 0,
          padding: 0,
          flexShrink: 0,
        },
        body: {
          flex: 1,
          minHeight: 0,
          height: "100%",
          overflow: "hidden",
          padding: "10px 0",
          display: "flex",
          flexDirection: "column",
        },
        footer: {
          margin: 0,
          padding: 0,
          flexShrink: 0,
        },
      }}
      title={
        <div className="flex flex-wrap items-center justify-between gap-3 border-gray-200 border-b pr-6 pb-2">
          <div className="flex items-center gap-2">
            <span className="font-bold text-gray-800 text-lg">📄 Chi tiết Log làm bài đọc #{record?.id}</span>
            {diffInfo && (
              <Tag color={diffInfo.color} className="font-medium text-xs">
                {diffInfo.label}
              </Tag>
            )}
            {record?.completion_number && (
              <Tag color="blue" className="font-medium font-mono text-xs">
                Lần #{record.completion_number}
              </Tag>
            )}
            {isInit ? (
              <Tag color="warning" className="font-semibold text-xs">
                Chưa hoàn thành / Rời bỏ
              </Tag>
            ) : (
              <>
                <Tag color="success" className="font-medium text-xs">
                  Hoàn thành
                </Tag>
                <Tag
                  color={accuracy >= 80 ? "success" : accuracy >= 50 ? "warning" : "error"}
                  className="font-mono font-semibold text-xs"
                >
                  Đúng {accuracy}% ({correct}/{total})
                </Tag>
              </>
            )}

            {totalRecords > 1 && typeof currentIndex === "number" && (
              <Space size={4} className="ml-2">
                <Button
                  size="small"
                  icon={<LeftOutlined />}
                  onClick={handlePrev}
                  disabled={!hasPrev}
                  title="Bản ghi trước (Phím ←)"
                >
                  Trước
                </Button>
                <span className="px-1 font-medium font-mono text-gray-500 text-xs">
                  {currentIndex + 1} / {totalRecords}
                </span>
                <Button
                  size="small"
                  icon={<RightOutlined />}
                  onClick={handleNext}
                  disabled={!hasNext}
                  title="Bản ghi tiếp theo (Phím →)"
                >
                  Tiếp
                </Button>
              </Space>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3 text-xs">
            <div className="flex items-center gap-1.5">
              <span className="text-gray-500">Người dùng:</span>
              <UserCellLink user={record?.user} userId={record?.user_id} fallback={<Tag color="default">Khách</Tag>} />
            </div>

            {(rc?.set?.full_title || rc?.set?.title) && (
              <>
                <Divider orientation="vertical" className="m-0" />
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">Bộ từ:</span>
                  {rc.set?.id ? (
                    <a
                      href={
                        rc.set.slug
                          ? `https://jupi.vn/set/${rc.set.slug}-${rc.set.id}`
                          : `https://jupi.vn/set/${rc.set.id}`
                      }
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold text-primary hover:underline"
                    >
                      {rc.set.full_title || rc.set.title}
                    </a>
                  ) : (
                    <strong className="text-gray-800">{rc.set.full_title || rc.set.title}</strong>
                  )}
                </div>
              </>
            )}

            {durationText && (
              <>
                <Divider orientation="vertical" className="m-0" />
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">Thời gian làm:</span>
                  <span className="font-mono font-semibold text-blue-600">{durationText}</span>
                </div>
              </>
            )}

            <Divider orientation="vertical" className="m-0" />

            <div className="flex items-center gap-1">
              <span className="text-gray-500">Bắt đầu:</span>
              <span className="font-mono text-gray-700">
                {record?.created_at ? dayjs(record.created_at).format("DD/MM/YYYY HH:mm:ss") : "—"}
              </span>
            </div>

            <Divider orientation="vertical" className="m-0" />

            <div className="flex items-center gap-1">
              <span className="text-gray-500">Hoàn thành:</span>
              <span className={record?.completed_at ? "font-mono text-gray-700" : "text-gray-400 italic"}>
                {record?.completed_at ? dayjs(record.completed_at).format("DD/MM/YYYY HH:mm:ss") : "Chưa nộp"}
              </span>
            </div>
          </div>
        </div>
      }
      footer={
        <div className="flex items-center justify-between border-gray-200 border-t pt-2">
          <div className="flex items-center gap-3 font-mono text-gray-500 text-xs">
            <span>Bài đọc ID: #{rc?.id || "—"}</span>
            {rc?.model && <span>Model AI: {rc.model}</span>}
            {rc?.genre && <span>Thể loại: {rc.genre}</span>}
          </div>
          <Button type="primary" onClick={onClose}>
            Đóng
          </Button>
        </div>
      }
    >
      {/* 2 COLUMNS SPLIT VIEW */}
      <div className="grid h-full min-h-0 grid-cols-1 gap-3 md:grid-cols-2">
        {/* LEFT COLUMN: PASSAGE & TARGET VOCABULARIES */}
        <div className="flex h-full min-h-0 flex-col rounded-lg border border-gray-200 bg-gray-50/50 p-2.5 shadow-xs">
          {/* Header Left */}
          <div className="mb-2 flex items-center justify-between border-gray-200 border-b pb-2">
            <div className="flex items-center gap-2">
              <BookOutlined className="text-base text-blue-500" />
              <span className="font-bold text-gray-800 text-sm">📖 NỘI DUNG BÀI ĐỌC</span>
              {rc?.part_number && (
                <Tag color="cyan" className="m-0 text-xs">
                  Part {rc.part_number}
                </Tag>
              )}
            </div>
            {rc?.title && (
              <span className="max-w-[200px] truncate font-semibold text-gray-700 text-xs" title={rc.title}>
                {rc.title}
              </span>
            )}
          </div>

          {/* Left Content Scrollable */}
          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
            {/* Passage Text Card */}
            <div className="rounded border border-gray-200 bg-white p-3.5 shadow-2xs">
              <div className="mb-2.5 flex items-center justify-between border-gray-100 border-b pb-1.5">
                <span className="flex items-center gap-1.5 font-semibold text-gray-700 text-xs">
                  <IconFileText size={16} className="text-blue-500" />
                  Đoạn văn bài đọc (Passage)
                </span>
                <span className="text-[11px] text-gray-400">
                  {rc?.passage_text?.split(/\s+/).filter(Boolean).length || 0} từ
                </span>
              </div>
              {highlightedPassage || <Empty description="Chưa có nội dung đoạn văn bài đọc" />}
            </div>

            {/* Target Vocabularies */}
            {rc?.target_vocabularies && rc.target_vocabularies.length > 0 && (
              <div className="rounded border border-gray-200 bg-white p-3 shadow-2xs">
                <div className="mb-2 flex items-center gap-1.5 font-semibold text-gray-700 text-xs">
                  <IconVocabulary size={16} className="text-amber-500" />
                  Từ vựng mục tiêu (Target Vocabularies - {rc.target_vocabularies.length} từ)
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {rc.target_vocabularies.map((vocab, idx) => (
                    <Tag key={idx} color="gold" className="m-0 border-amber-300 px-2 py-0.5 font-medium text-xs">
                      {vocab}
                    </Tag>
                  ))}
                </div>
              </div>
            )}

            {/* Word Bank */}
            {rc?.word_bank && rc.word_bank.length > 0 && (
              <div className="rounded border border-gray-200 bg-white p-3 shadow-2xs">
                <div className="mb-2 flex items-center gap-1.5 font-semibold text-gray-700 text-xs">
                  <IconBulb size={16} className="text-purple-500" />
                  Kho từ gợi ý (Word Bank - {rc.word_bank.length} từ)
                </div>
                <div className="flex flex-wrap gap-1">
                  {rc.word_bank.map((w, idx) => (
                    <Tag key={idx} color="purple" className="m-0 text-xs">
                      {w}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: PRACTICE RESULTS, BREAKDOWN & USER ANSWERS */}
        <div className="flex h-full min-h-0 flex-col rounded-lg border border-gray-200 bg-gray-50/50 p-2.5 shadow-xs">
          {/* Header Right */}
          <div className="mb-2 flex items-center justify-between border-gray-200 border-b pb-2">
            <div className="flex items-center gap-2">
              <span className="font-bold text-gray-800 text-sm">📊 KẾT QUẢ & PHÂN TÍCH BÀI LÀM</span>
            </div>
            <div className="flex items-center gap-1">
              {record?.selected_types?.map((t) => {
                const info = QUESTION_TYPE_MAP[t]
                return (
                  <Tag key={t} color={info?.color || "default"} className="m-0 text-[10px]">
                    {info?.label || t}
                  </Tag>
                )
              })}
            </div>
          </div>

          {/* Right Column Content */}
          {isInit ? (
            /* CASE: STATUS = INIT (CHƯA NỘP BÀI) */
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
              <Alert
                message={<span className="font-bold text-amber-800">Học viên chưa nộp bài này</span>}
                description={
                  <div className="mt-0.5 flex flex-col gap-1 text-amber-700 text-xs">
                    <div>
                      Bắt đầu lúc:{" "}
                      <strong className="font-mono text-gray-800">
                        {record?.created_at ? dayjs(record.created_at).format("DD/MM/YYYY HH:mm:ss") : "—"}
                      </strong>
                    </div>
                    <div className="text-gray-500">
                      Học viên bấm vào học nhưng chưa bấm nộp bài (đang làm dở hoặc đã thoát ra ngoài). Hệ thống chưa
                      ghi nhận điểm số và đáp án hoàn thành.
                    </div>
                  </div>
                }
                type="warning"
                showIcon
                className="border-amber-200 bg-amber-50"
              />

              {/* Show questions for reference */}
              <div className="flex flex-col gap-3 rounded border border-gray-200 bg-white p-3 shadow-2xs">
                <div className="flex items-center gap-1.5 border-gray-100 border-b pb-1.5 font-semibold text-gray-700 text-xs">
                  <QuestionCircleOutlined className="text-blue-500" />
                  Nội dung câu hỏi bài tập (Đề bài & Đáp án chuẩn)
                </div>

                {/* Cloze */}
                {clozeQuestions.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <span className="w-fit rounded bg-blue-50 px-2 py-0.5 font-semibold text-blue-700 text-xs">
                      1. Điền từ vào chỗ trống (Cloze - {clozeQuestions.length} câu)
                    </span>
                    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                      {clozeQuestions.map((q, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 rounded border border-gray-200 bg-gray-50 p-1.5 text-xs"
                        >
                          <Badge count={q.index ?? idx + 1} style={{ backgroundColor: "#1890ff" }} />
                          <div className="flex flex-col">
                            <span className="font-semibold text-emerald-700">Đáp án chuẩn: {q.correct_answer}</span>
                            {q.accepted_answers && q.accepted_answers.length > 0 && (
                              <span className="text-[11px] text-gray-500">
                                Chấp nhận: {q.accepted_answers.join(", ")}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* MCQ */}
                {mcqQuestions.length > 0 && (
                  <div className="mt-1 flex flex-col gap-2">
                    <span className="w-fit rounded bg-purple-50 px-2 py-0.5 font-semibold text-purple-700 text-xs">
                      2. Trắc nghiệm (MCQ - {mcqQuestions.length} câu)
                    </span>
                    <div className="flex flex-col gap-2">
                      {mcqQuestions.map((q, idx) => (
                        <div
                          key={idx}
                          className="flex flex-col gap-1.5 rounded border border-gray-200 bg-gray-50 p-2.5 text-xs"
                        >
                          <div className="font-semibold text-gray-800">
                            Câu {idx + 1}: {q.question}
                          </div>
                          <div className="grid grid-cols-1 gap-1 pl-2 sm:grid-cols-2">
                            {q.options?.map((opt) => {
                              const isCorrect = opt.key === q.answer
                              return (
                                <div
                                  key={opt.key}
                                  className={`rounded px-2 py-1 text-[11px] ${
                                    isCorrect
                                      ? "border border-emerald-300 bg-emerald-100 font-semibold text-emerald-800"
                                      : "border border-gray-200 bg-white text-gray-700"
                                  }`}
                                >
                                  <strong>{opt.key}.</strong> {opt.text}
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* TF */}
                {tfQuestions.length > 0 && (
                  <div className="mt-1 flex flex-col gap-2">
                    <span className="w-fit rounded bg-cyan-50 px-2 py-0.5 font-semibold text-cyan-700 text-xs">
                      3. Đúng / Sai (True-False - {tfQuestions.length} câu)
                    </span>
                    <div className="flex flex-col gap-2">
                      {tfQuestions.map((q, idx) => (
                        <div
                          key={idx}
                          className="flex flex-col gap-1.5 rounded border border-gray-200 bg-gray-50 p-2.5 text-xs"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <span className="font-semibold text-gray-800">
                              Câu {idx + 1}: {q.statement}
                            </span>
                            <Tag
                              color={String(q.answer).toLowerCase() === "true" ? "success" : "error"}
                              className="m-0 font-bold font-mono"
                            >
                              {String(q.answer).toUpperCase()}
                            </Tag>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* CASE: STATUS = COMPLETED (ĐÃ NỘP BÀI) */
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
              {/* Quick Result Summary Cards */}
              <div className="grid grid-cols-4 gap-2 rounded border border-gray-200 bg-white p-2 text-center text-xs">
                <div className="flex flex-col items-center justify-center border-gray-100 border-r p-1">
                  <span className="text-[11px] text-gray-500">Độ chính xác</span>
                  <span
                    className={`font-bold font-mono text-base ${accuracy >= 80 ? "text-emerald-600" : accuracy >= 50 ? "text-amber-600" : "text-rose-600"}`}
                  >
                    {accuracy}%
                  </span>
                </div>
                <div className="flex flex-col items-center justify-center border-gray-100 border-r p-1">
                  <span className="flex items-center gap-0.5 font-medium text-[11px] text-emerald-600">
                    <IconCheck size={12} /> Đúng
                  </span>
                  <span className="font-bold font-mono text-base text-emerald-600">{correct} câu</span>
                </div>
                <div className="flex flex-col items-center justify-center border-gray-100 border-r p-1">
                  <span className="flex items-center gap-0.5 font-medium text-[11px] text-rose-600">
                    <IconX size={12} /> Sai
                  </span>
                  <span className="font-bold font-mono text-base text-rose-600">{wrong} câu</span>
                </div>
                <div className="flex flex-col items-center justify-center p-1">
                  <span className="flex items-center gap-0.5 font-medium text-[11px] text-amber-600">
                    <IconMinus size={12} /> Bỏ qua
                  </span>
                  <span className="font-bold font-mono text-amber-600 text-base">{skipped} câu</span>
                </div>
              </div>

              {/* Breakdown Table */}
              {record?.breakdown && record.breakdown.length > 0 && (
                <div className="overflow-hidden rounded border border-gray-200 bg-white shadow-2xs">
                  <div className="border-gray-100 border-b bg-gray-50 px-3 py-1.5 font-semibold text-gray-700 text-xs">
                    Phân tích theo từng dạng bài (Breakdown)
                  </div>
                  <Table
                    columns={breakdownColumns}
                    dataSource={record.breakdown}
                    rowKey="typeName"
                    pagination={false}
                    size="small"
                  />
                </div>
              )}

              {/* Questions & Answer Key Sections with Student Answers */}
              <div className="flex flex-col gap-3 rounded border border-gray-200 bg-white p-3 shadow-2xs">
                <div className="flex items-center justify-between border-gray-100 border-b pb-1.5 font-semibold text-gray-700 text-xs">
                  <span className="flex items-center gap-1.5">
                    <QuestionCircleOutlined className="text-blue-500" />
                    Nội dung câu hỏi & Đối chiếu đáp án
                  </span>
                  <span className="text-[11px] text-gray-400">Đã làm trong: {durationText || "—"}</span>
                </div>

                {/* 1. Cloze Questions */}
                {clozeQuestions.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <span className="w-fit rounded bg-blue-50 px-2 py-0.5 font-semibold text-blue-700 text-xs">
                      1. Điền từ vào chỗ trống (Cloze - {clozeQuestions.length} câu)
                    </span>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {clozeQuestions.map((q, idx) => {
                        const userAns = userCloze[String(q.index ?? idx + 1)] ?? userCloze[String(idx + 1)]
                        const hasAns = userAns != null && String(userAns).trim() !== ""
                        const isCorrect = hasAns && isClozeAnswerCorrect(q, userAns)

                        return (
                          <div
                            key={idx}
                            className="flex flex-col gap-1 rounded border border-gray-200 bg-gray-50 p-2 text-xs"
                          >
                            <div className="flex items-center justify-between">
                              <Badge count={q.index ?? idx + 1} style={{ backgroundColor: "#1890ff" }} />
                              {hasAns ? (
                                isCorrect ? (
                                  <Tag color="success" className="m-0 text-[10px]">
                                    Đúng
                                  </Tag>
                                ) : (
                                  <Tag color="error" className="m-0 text-[10px]">
                                    Sai
                                  </Tag>
                                )
                              ) : (
                                <Tag color="default" className="m-0 text-[10px]">
                                  Bỏ qua
                                </Tag>
                              )}
                            </div>
                            <div className="mt-1">
                              <div className="text-[11px] text-gray-500">Học viên điền:</div>
                              <div
                                className={`font-mono font-semibold ${hasAns ? (isCorrect ? "text-emerald-700" : "text-rose-600 line-through") : "text-gray-400 italic"}`}
                              >
                                {hasAns ? userAns : "(Chưa điền)"}
                              </div>
                            </div>
                            <div className="mt-0.5 border-gray-200/60 border-t pt-1">
                              <div className="text-[11px] text-gray-500">Đáp án chuẩn:</div>
                              <div className="font-mono font-semibold text-emerald-800">{q.correct_answer}</div>
                              {q.accepted_answers && q.accepted_answers.length > 0 && (
                                <div className="text-[10px] text-gray-500">
                                  Chấp nhận: {q.accepted_answers.join(", ")}
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* 2. Multiple Choice Questions (MCQ) */}
                {mcqQuestions.length > 0 && (
                  <div className="mt-1 flex flex-col gap-2">
                    <span className="w-fit rounded bg-purple-50 px-2 py-0.5 font-semibold text-purple-700 text-xs">
                      2. Trắc nghiệm (MCQ - {mcqQuestions.length} câu)
                    </span>
                    <div className="flex flex-col gap-2">
                      {mcqQuestions.map((q, idx) => {
                        const userKey = userMcq[String(q.id)] ?? userMcq[String(idx + 1)]
                        const hasAns = userKey != null && String(userKey).trim() !== ""
                        const isCorrect = hasAns && isMcqAnswerCorrect(q.answer, userKey)

                        return (
                          <div
                            key={idx}
                            className="flex flex-col gap-1.5 rounded border border-gray-200 bg-gray-50 p-2.5 text-xs"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <span className="font-semibold text-gray-800">
                                Câu {idx + 1}: {q.question}
                              </span>
                              {hasAns ? (
                                isCorrect ? (
                                  <Tag color="success" className="m-0 text-[10px]">
                                    Đúng
                                  </Tag>
                                ) : (
                                  <Tag color="error" className="m-0 text-[10px]">
                                    Sai
                                  </Tag>
                                )
                              ) : (
                                <Tag color="default" className="m-0 text-[10px]">
                                  Bỏ qua
                                </Tag>
                              )}
                            </div>

                            <div className="grid grid-cols-1 gap-1 pl-2 sm:grid-cols-2">
                              {q.options?.map((opt) => {
                                const isRightKey = opt.key.toLowerCase() === q.answer.toLowerCase()
                                const isUserChosen = hasAns && opt.key.toLowerCase() === userKey.toLowerCase()

                                let bgClass = "bg-white text-gray-700 border-gray-200"
                                if (isRightKey && isUserChosen) {
                                  bgClass =
                                    "bg-emerald-100 text-emerald-900 border-emerald-400 font-semibold ring-1 ring-emerald-400"
                                } else if (isRightKey) {
                                  bgClass = "bg-emerald-50 text-emerald-800 border-emerald-300 font-medium"
                                } else if (isUserChosen) {
                                  bgClass = "bg-rose-50 text-rose-800 border-rose-300 font-medium ring-1 ring-rose-400"
                                }

                                return (
                                  <div
                                    key={opt.key}
                                    className={`flex items-center justify-between rounded border px-2 py-1 text-[11px] ${bgClass}`}
                                  >
                                    <span>
                                      <strong>{opt.key}.</strong> {opt.text}
                                    </span>
                                    {isUserChosen && (
                                      <Tag
                                        color={isRightKey ? "success" : "error"}
                                        className="m-0 px-1 py-0 text-[9px]"
                                      >
                                        Học viên chọn
                                      </Tag>
                                    )}
                                  </div>
                                )
                              })}
                            </div>

                            {q.explanation && (
                              <div className="mt-1 rounded border border-blue-100 bg-blue-50/60 p-1.5 text-[11px] text-gray-600">
                                💡 <strong>Giải thích:</strong> {q.explanation}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* 3. True / False Questions (TF) */}
                {tfQuestions.length > 0 && (
                  <div className="mt-1 flex flex-col gap-2">
                    <span className="w-fit rounded bg-cyan-50 px-2 py-0.5 font-semibold text-cyan-700 text-xs">
                      3. Đúng / Sai (True-False - {tfQuestions.length} câu)
                    </span>
                    <div className="flex flex-col gap-2">
                      {tfQuestions.map((q, idx) => {
                        const userAns = userTf[String(q.id)] ?? userTf[String(idx + 1)]
                        const hasAns = userAns != null && String(userAns).trim() !== ""
                        const isCorrect = hasAns && isTfAnswerCorrect(q.answer, userAns)

                        return (
                          <div
                            key={idx}
                            className="flex flex-col gap-1.5 rounded border border-gray-200 bg-gray-50 p-2.5 text-xs"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <span className="font-semibold text-gray-800">
                                Câu {idx + 1}: {q.statement}
                              </span>
                              {hasAns ? (
                                isCorrect ? (
                                  <Tag color="success" className="m-0 text-[10px]">
                                    Đúng
                                  </Tag>
                                ) : (
                                  <Tag color="error" className="m-0 text-[10px]">
                                    Sai
                                  </Tag>
                                )
                              ) : (
                                <Tag color="default" className="m-0 text-[10px]">
                                  Bỏ qua
                                </Tag>
                              )}
                            </div>

                            <div className="flex items-center gap-3 rounded border border-gray-200 bg-white p-2 text-[11px]">
                              <div className="flex items-center gap-1.5">
                                <span className="text-gray-500">Học viên chọn:</span>
                                {hasAns ? (
                                  <Tag color={isCorrect ? "success" : "error"} className="m-0 font-bold font-mono">
                                    {String(userAns).toUpperCase()}
                                  </Tag>
                                ) : (
                                  <span className="text-gray-400 italic">(Bỏ qua)</span>
                                )}
                              </div>

                              <Divider orientation="vertical" className="m-0" />

                              <div className="flex items-center gap-1.5">
                                <span className="text-gray-500">Đáp án chuẩn:</span>
                                <Tag color="blue" className="m-0 font-bold font-mono">
                                  {String(q.answer).toUpperCase()}
                                </Tag>
                              </div>
                            </div>

                            {q.explanation && (
                              <div className="rounded border border-blue-100 bg-blue-50/60 p-1.5 text-[11px] text-gray-600">
                                💡 <strong>Giải thích:</strong> {q.explanation}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default ReadingPracticeDetailModal
